import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Tabs,
  Button,
  Table,
  Tooltip,
  message,
  Input,
  Select,
  DatePicker,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import { useGetState, useSize, useDebounce, useUpdateEffect } from "ahooks";
import { useDispatch } from "react-redux";
import { setBreadcrumbList } from "../../store/menuInfo/menuInfo";
import apis from "../../apis";
import TocManualOrderPage from "./TocManualOrderPage";
import MessageModal from "../../components/MessageModal";
import "./index.scss";

const { Paragraph } = Typography;

const IconView = (
  <svg
    t="1704265207410"
    viewBox="0 0 1024 1024"
    version="1.1"
    p-id="4261"
    width="12"
    height="12"
    style={{ cursor: "pointer" }}
  >
    <path
      d="M896 64H128c-35.296 0-64 28.704-64 64v768c0 35.296 28.704 64 64 64h592a32 32 0 1 0 0-64H128V128h768v592a32 32 0 1 0 64 0V128c0-35.296-28.704-64-64-64z"
      p-id="4262"
    ></path>
    <path
      d="M791.744 746.496A206.752 206.752 0 0 0 832 624c0-114.688-93.312-208-208-208S416 509.312 416 624s93.312 208 208 208a206.752 206.752 0 0 0 122.496-40.256l110.88 110.88a31.904 31.904 0 0 0 45.248 0 31.968 31.968 0 0 0 0-45.248l-110.88-110.88zM480 624c0-79.392 64.608-144 144-144s144 64.608 144 144-64.608 144-144 144-144-64.608-144-144zM800 264a32 32 0 0 0-32-32H256a32 32 0 0 0 0 64h512a32 32 0 0 0 32-32zM256 422.656a32 32 0 0 0 0 64h96a32 32 0 0 0 0-64H256z"
      p-id="4263"
    ></path>
  </svg>
);

const IconDelete = (
  <svg
    t="1705891931298"
    viewBox="0 0 1024 1024"
    version="1.1"
    p-id="5147"
    width="14"
    height="14"
    style={{ cursor: "pointer" }}
  >
    <path
      d="M607.897867 768.043004c-17.717453 0-31.994625-14.277171-31.994625-31.994625L575.903242 383.935495c0-17.717453 14.277171-31.994625 31.994625-31.994625s31.994625 14.277171 31.994625 31.994625l0 351.94087C639.892491 753.593818 625.61532 768.043004 607.897867 768.043004z"
      fill="#575B66"
      p-id="5148"
    ></path>
    <path
      d="M415.930119 768.043004c-17.717453 0-31.994625-14.277171-31.994625-31.994625L383.935495 383.935495c0-17.717453 14.277171-31.994625 31.994625-31.994625 17.717453 0 31.994625 14.277171 31.994625 31.994625l0 351.94087C447.924744 753.593818 433.647573 768.043004 415.930119 768.043004z"
      fill="#575B66"
      p-id="5149"
    ></path>
    <path
      d="M928.016126 223.962372l-159.973123 0L768.043004 159.973123c0-52.980346-42.659499-95.983874-95.295817-95.983874L351.94087 63.989249c-52.980346 0-95.983874 43.003528-95.983874 95.983874l0 63.989249-159.973123 0c-17.717453 0-31.994625 14.277171-31.994625 31.994625s14.277171 31.994625 31.994625 31.994625l832.032253 0c17.717453 0 31.994625-14.277171 31.994625-31.994625S945.73358 223.962372 928.016126 223.962372zM319.946246 159.973123c0-17.545439 14.449185-31.994625 31.994625-31.994625l320.806316 0c17.545439 0 31.306568 14.105157 31.306568 31.994625l0 63.989249L319.946246 223.962372 319.946246 159.973123 319.946246 159.973123z"
      fill="#575B66"
      p-id="5150"
    ></path>
    <path
      d="M736.048379 960.010751 288.123635 960.010751c-52.980346 0-95.983874-43.003528-95.983874-95.983874L192.139761 383.591466c0-17.717453 14.277171-31.994625 31.994625-31.994625s31.994625 14.277171 31.994625 31.994625l0 480.435411c0 17.717453 14.449185 31.994625 31.994625 31.994625l448.096758 0c17.717453 0 31.994625-14.277171 31.994625-31.994625L768.215018 384.795565c0-17.717453 14.277171-31.994625 31.994625-31.994625s31.994625 14.277171 31.994625 31.994625l0 479.231312C832.032253 916.835209 789.028725 960.010751 736.048379 960.010751z"
      fill="#575B66"
      p-id="5151"
    ></path>
  </svg>
);

const TocOrderManagement = () => {
  const dispatch = useDispatch();
  const [viewState, setViewState] = useState("list"); // 'list' | 'manual' | 'view'
  const [currentRecord, setCurrentRecord] = useState(null);
  const [mode, setMode] = useState("enterprise"); // 'enterprise' | 'personal'
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [productList, setProductList] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Filters
  const [orderCode, setOrderCode, getOrderCode] = useGetState("");
  const [orderName, setOrderName, getOrderName] = useGetState("");
  const [orderTimeRange, setOrderTimeRange, getOrderTimeRange] = useGetState([]);

  // Enterprise specific
  const [companyId, setCompanyId, getCompanyId] = useGetState(null);
  const [companyList, setCompanyList] = useState([]);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);

  // Personal specific
  const [userId, setUserId, getUserId] = useGetState(null);
  const [userList, setUserList] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const [pageNumber, setPageNumber, getPageNumber] = useGetState(1);
  const [pageSize, setPageSize, getPageSize] = useGetState(10);

  const refTable = useRef(null);
  const refHeader = useRef(null);
  const size = useSize(refTable);
  const headerSize = useSize(refHeader);

  const debouncedOrderCode = useDebounce(orderCode, { wait: 500 });
  const debouncedOrderName = useDebounce(orderName, { wait: 500 });

  // 获取产品列表
  useEffect(() => {
    apis.getProductList("TOC").then((res) => {
      if (res.code === 200) {
        setProductList(res.data || []);
        // 产品不设置默认值，由用户手动选择
      }
    });
  }, []);

  // Fetch data
  const getDataFunc = () => {
    setLoading(true);
    const isEnterprise = mode === "enterprise";
    const params = {
      order_type: isEnterprise ? "ENTERPRISE" : "PERSONAL",
      toc_co_id: isEnterprise ? getCompanyId() : undefined,
      co_user_id: !isEnterprise ? getUserId() : undefined,
      order_code: getOrderCode() || undefined,
      order_name: getOrderName() || undefined,
      order_time_start: getOrderTimeRange()?.[0]
        ? dayjs(getOrderTimeRange()[0]).format("YYYY-MM-DD HH:mm:ss")
        : undefined,
      order_time_end: getOrderTimeRange()?.[1]
        ? dayjs(getOrderTimeRange()[1]).format("YYYY-MM-DD HH:mm:ss")
        : undefined,
      page: getPageNumber(),
      page_size: getPageSize(),
    };

    // Remove undefined params
    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key]
    );

    apis
      .getTocOrderList(params)
      .then((res) => {
        if (res.code === 200) {
          setData(res.data?.data || res.data?.items || []);
          setTotal(res.data?.total_count || res.data?.total || 0);
        } else {
          setData([]);
          setTotal(0);
          message.error(res.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (viewState === "list") {
      dispatch(setBreadcrumbList([{ title: "TOC订单管理" }]));
    } else if (viewState === "manual") {
      dispatch(
        setBreadcrumbList([
          { title: "TOC订单管理", onClick: () => setViewState("list") },
          { title: "手工下单" },
        ])
      );
    } else if (viewState === "view") {
      dispatch(
        setBreadcrumbList([
          { title: "TOC订单管理", onClick: () => setViewState("list") },
          { title: "查看详情" },
        ])
      );
    }
  }, [viewState]);

  useEffect(() => {
    if (viewState === "list") {
      getDataFunc();
    }
  }, [pageNumber, pageSize, mode]);

  useUpdateEffect(() => {
    if (viewState === "list") {
      if (getPageNumber() === 1) {
        getDataFunc();
      } else {
        setPageNumber(1);
      }
    }
  }, [
    debouncedOrderCode,
    debouncedOrderName,
    orderTimeRange,
    companyId,
    userId,
  ]);

  // Search helpers
  const fetchCompanyList = (value) => {
    if (!value) {
      setCompanyList([]);
      return;
    }
    setCompanySearchLoading(true);
    apis
      .getTocCompanyList({ keyword: value, page: 1, page_size: 50 })
      .then((res) => {
        setCompanySearchLoading(false);
        if (res.code === 200) {
          setCompanyList(res.data?.data || []);
        }
      })
      .catch(() => {
        setCompanySearchLoading(false);
        setCompanyList([]);
      });
  };

  const fetchUserList = (value) => {
    if (!value) {
      setUserList([]);
      return;
    }
    setUserSearchLoading(true);
    apis
      .searchTocUserByPhone({ phone: value })
      .then((res) => {
        setUserSearchLoading(false);
        if (res.code === 200) {
          setUserList(res.data || []);
        }
      })
      .catch(() => {
        setUserSearchLoading(false);
        setUserList([]);
      });
  };

  const debounceCompanyFetcher = useMemo(() => {
    return debounce(fetchCompanyList, 500);
  }, []);

  const debounceUserFetcher = useMemo(() => {
    return debounce(fetchUserList, 500);
  }, []);

  const handleSearchCompany = (value) => {
    debounceCompanyFetcher(value);
  };

  const handleSearchUser = (value) => {
    debounceUserFetcher(value);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setViewState("view");
  };

  const handleCancel = (record) => {
    apis
      .cancelTocManualTopup({ order_id: record.order_id })
      .then((res) => {
        if (res.code === 200) {
          message.success("撤销成功");
          getDataFunc();
        } else {
          message.error(res.message);
        }
      })
      .catch(() => {
        message.error("撤销失败");
      });
  };

  const handleDownload = () => {
    const isEnterprise = mode === "enterprise";
    const params = {
      order_type: isEnterprise ? "ENTERPRISE" : "PERSONAL",
      toc_co_id: isEnterprise ? getCompanyId() : undefined,
      co_user_id: !isEnterprise ? getUserId() : undefined,
      order_code: getOrderCode() || undefined,
      order_name: getOrderName() || undefined,
      order_time_start: getOrderTimeRange()?.[0]
        ? dayjs(getOrderTimeRange()[0]).format("YYYY-MM-DD HH:mm:ss")
        : undefined,
      order_time_end: getOrderTimeRange()?.[1]
        ? dayjs(getOrderTimeRange()[1]).format("YYYY-MM-DD HH:mm:ss")
        : undefined,
    };

    apis
      .downloadTocOrderList(params)
      .then((res) => {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        let fileName = `TOC订单_${dayjs().format("YYYYMMDDHHmmss")}.xlsx`;
        const contentDisposition = res.headers["content-disposition"];
        if (contentDisposition) {
          // 先尝试匹配 filename*=UTF-8'' 格式 (RFC 5987)
          const filenameStarRegex = /filename\*=(?:UTF-8'')?([^;\n]*)/i;
          const starMatch = filenameStarRegex.exec(contentDisposition);
          if (starMatch && starMatch[1]) {
            const encodedFileName = starMatch[1].replace(/^UTF-8''/, '').replace(/['"]/g, '');
            try {
              fileName = decodeURIComponent(encodedFileName);
            } catch {
              fileName = encodedFileName;
            }
          } else {
            // 回退匹配普通 filename= 格式
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDisposition);
            if (matches != null && matches[1]) {
              fileName = decodeURIComponent(matches[1].replace(/['"]/g, ""));
            }
          }
        }

        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(link.href);
      })
      .catch(() => {
        message.error("下载失败");
      });
  };

  const commonColumns = [
    {
      title: "订单编号",
      dataIndex: "order_code",
      key: "order_code",
      align: "center",
      render: (text) => (
        <Paragraph
          ellipsis={{ rows: 2, tooltip: { title: text } }}
          style={{ marginBottom: 0 }}
        >
          {text || "-"}
        </Paragraph>
      ),
    },
    {
      title: "订单类型",
      dataIndex: "order_type_name",
      key: "order_type_name",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "订单名称",
      dataIndex: "order_name",
      key: "order_name",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "订单金额(元)",
      dataIndex: "order_amount",
      key: "order_amount",
      align: "center",
      render: (text) => (text !== undefined && text !== null ? parseFloat(text).toFixed(2) : "-"),
    },
    {
      title: "支付金额(元)",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "center",
      render: (text) => (text !== undefined && text !== null ? parseFloat(text).toFixed(2) : "-"),
    },
    {
      title: "下单时间",
      dataIndex: "order_time",
      key: "order_time",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "下单方式",
      dataIndex: "order_method_name",
      key: "order_method_name",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "支付方式",
      dataIndex: "payment_method_name",
      key: "payment_method_name",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "订单状态",
      dataIndex: "order_status_name",
      key: "order_status_name",
      align: "center",
      render: (text, record) => {
        if (record.order_status_code === "CANCELED") {
          return <span style={{ color: '#ff4d4f' }}>{text || "-"}</span>;
        }
        return text || "-";
      },
    },
    {
      title: "下单人",
      dataIndex: "orderer_name",
      key: "orderer_name",
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) => (
        <div className="tableTagsBox">
          <Tooltip title="详情">
            <span onClick={() => handleView(record)} className="action-icon">
              {IconView}
            </span>
          </Tooltip>
          {record.order_status_code === "COMPLETED" && (
            <Tooltip title="撤销">
              <span
                onClick={() => {
                  MessageModal({
                    type: "warning",
                    title: "撤销",
                    noIcon: true,
                    description: `确定撤销该订单吗？`,
                    onOk: () => handleCancel(record),
                  });
                }}
                className="action-icon"
              >
                {IconDelete}
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const columns = useMemo(() => {
    const cols = [...commonColumns];
    if (mode === "enterprise") {
      cols.splice(3, 0, {
        title: "企业(编号/名称)",
        key: "enterprise_display",
        dataIndex: "enterprise_display",
        align: "center",
        render: (_, record) => {
          if (record.co_code && record.co_name) {
            return `${record.co_code} / ${record.co_name}`;
          }
          if (record.co_code) return record.co_code;
          if (record.co_name) return record.co_name;
          return "-";
        },
      });
    } else {
      cols.splice(3, 0, {
        title: "用户(手机号/昵称)",
        key: "user_display",
        align: "center",
        render: (_, record) => {
          const phone = record.user_phone || "-";
          const nickname = record.nickname || "-";
          return `${phone} / ${nickname}`;
        },
      });
    }
    return cols;
  }, [mode]);

  if (viewState === "manual" || viewState === "view") {
    return (
      <TocManualOrderPage
        mode={mode}
        productList={productList}
        selectedProductId={selectedProductId}
        onProductChange={setSelectedProductId}
        id={currentRecord?.order_id}
        record={currentRecord}
        type={viewState}
        onBack={() => {
          setViewState("list");
          setSelectedProductId(null);
        }}
        onSuccess={() => {
          setViewState("list");
          setSelectedProductId(null);
          getDataFunc();
        }}
      />
    );
  }

  return (
    <div className="topupManagementContent" ref={refTable}>
      <div ref={refHeader} className="filterItemsBox">
        <Tabs
          activeKey={mode}
          onChange={(key) => {
            setMode(key);
            setPageNumber(1);
            setData([]);
            setTotal(0);
            setUserId(null);
            setCompanyId(null);
            setUserList([]);
            setCompanyList([]);
          }}
          className="modeTabs"
          items={[
            { key: "enterprise", label: "企业" },
            { key: "personal", label: "个人" },
          ]}
          style={{ width: "100%" }}
        />

        <div className="filterItemsContent">
          {mode === "enterprise" ? (
            <div className="filterItem">
              <span className="filterItemSpan">企业</span>
              <Select
                showSearch
                placeholder="输入所有者手机号或企业名称、编号搜索"
                value={companyId}
                onChange={(value) => {
                  setCompanyId(value);
                  setPageNumber(1);
                }}
                onSearch={handleSearchCompany}
                filterOption={false}
                allowClear
                style={{ width: 240 }}
                popupMatchSelectWidth={false}
                notFoundContent={null}
                loading={companySearchLoading}
                onClear={() => {
                  setCompanyList([]);
                  setCompanyId(null);
                  setPageNumber(1);
                  getDataFunc();
                }}
                labelRender={
                  (option) => {
                      const info = JSON.parse(option.label || '');
                      return (
                          <div className="PersonAndCompanySelectItem3">
                              <a className="nameBox" title={info.toc_co_name}>
                                  <div className='nameBoxContent' style={{ fontWeight: 'unset' }}>{info.toc_co_name}</div>
                              </a>
                              <span className="codeBox">企业编号:{info.toc_co_code}</span>
                          </div>
                      )
                  }
              }
              optionRender={(option) => {
                  const info = JSON.parse(option.label || '');
                  return (
                    <div>
                      <div className="PersonAndCompanySelectItem3">
                          <a className="nameBox" title={info.toc_co_name}>
                              <div className='nameBoxContent'>{info.toc_co_name}</div>
                          </a>
                          <span className="codeBox">企业编号:{info.toc_co_code}</span>
                      </div>
                      <div className='ownerBox'>所有者:{info.user_phone}/{info.user_name}</div>
                    </div>
                  )
              }}
                options={companyList.map((c) => ({
                  value: c.toc_co_id,
                  label: JSON.stringify(c)
                }))}
              />
            </div>
          ) : (
            <div className="filterItem">
              <span className="filterItemSpan">用户</span>
              <Select
                showSearch
                placeholder="请输入手机号查询"
                value={userId}
                onChange={(value) => {
                  setUserId(value);
                  setPageNumber(1);
                }}
                onSearch={handleSearchUser}
                filterOption={false}
                allowClear
                style={{ width: 200 }}
                loading={userSearchLoading}
                notFoundContent={null}
                onClear={() => {
                  setUserList([]);
                  setUserId(null);
                  setPageNumber(1);
                  getDataFunc();
                }}
                options={userList.map((u) => ({
                  value: u.user_id,
                  label: (
                    <div className="PersonAndCompanySelectItem2">
                      <span className="mobileBox" title={u.mobile_no || u.phone}>
                        {u.mobile_no || u.phone}
                      </span>
                      <span
                        className="nameBox"
                        title={u.user_nickname || u.nickname || u.show_name}
                        style={{ textAlign: "right" }}
                      >
                        {u.user_nickname || u.nickname || u.show_name}
                      </span>
                    </div>
                  ),
                }))}
              />
            </div>
          )}

          <div className="filterItem">
            <span className="filterItemSpan">订单编号</span>
            <Input
              placeholder="请输入"
              value={orderCode}
              onChange={(e) => setOrderCode(e.target.value)}
              allowClear
              style={{ width: 150 }}
            />
          </div>

          <div className="filterItem">
            <span className="filterItemSpan">订单名称</span>
            <Input
              placeholder="请输入"
              value={orderName}
              onChange={(e) => setOrderName(e.target.value)}
              allowClear
              style={{ width: 150 }}
            />
          </div>

          <div className="filterItem">
            <span className="filterItemSpan">下单时间</span>
            <DatePicker
              value={orderTimeRange?.[0]}
              onChange={(date) => {
                const newRange = [...(orderTimeRange || [])];
                newRange[0] = date;
                setOrderTimeRange(newRange);
                setPageNumber(1);
              }}
              style={{ width: 120 }}
              placeholder="开始日期"
              disabledDate={(current) => {
                const end = orderTimeRange?.[1];
                return end && current && current > end.endOf('day');
              }}
            />
            &nbsp;&nbsp;~&nbsp;&nbsp;
            <DatePicker
              value={orderTimeRange?.[1]}
              onChange={(date) => {
                const newRange = [...(orderTimeRange || [])];
                newRange[1] = date;
                setOrderTimeRange(newRange);
                setPageNumber(1);
              }}
              style={{ width: 120 }}
              placeholder="结束日期"
              disabledDate={(current) => {
                const start = orderTimeRange?.[0];
                return start && current && current < start.startOf('day');
              }}
            />
          </div>

          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              type="primary"
              className="btn"
              onClick={() => setViewState("manual")}
            >
              手工下单
            </Button>
            <Button type="primary" className="btn" onClick={handleDownload}>
              下载
            </Button>
          </div>
        </div>
      </div>

      <div className="tableContent">
        <Table
          className="myFormTable"
          dataSource={data}
          columns={columns}
          rowKey="order_id"
          loading={loading}
          bordered={true}
          scroll={{
            y: size?.height
              ? size.height - (headerSize?.height || 0) - 180 + "px"
              : undefined,
          }}
          pagination={{
            current: pageNumber,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => {
              setPageNumber(page);
              setPageSize(size);
            },
          }}
        />
      </div>
    </div>
  );
};

export default TocOrderManagement;
